import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from './ui/dialog';
import type { FieldConfiguration, AssignedUser } from '@/types/workflow';
import { CustomDropdownField } from './custom-dropdown-field';
import { defaultBooleanOptions } from './constants';
import { useToast } from '@/hooks/use-toast';
import { MultiSelectCell } from './multi-select-cell';
import { ModernDatePicker } from './modern-date-picker';
import { isBooleanField, isDropdownField, isTextListField, isDateField, isNumberField, getSearchColumnPath, getFacetKeysFromConfiguration } from '@/utils/column-type-helpers';
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking';
import { useWorkflow } from '@/context/workflow-context-simple';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: string[];
  bulkUpdateReports: (updates: { id: string; updates: Record<string, any> }[]) => Promise<any>;
  updateWorkOrdersOptimistically: (updates: { id: string; updates: Record<string, any> }[]) => void;
  setSelectedItems: (items: string[]) => void;
  t: (key: string) => string;
  fields: FieldConfiguration[];
  language: string;
  assignedUsers: AssignedUser[];
  refreshData: (customFilterArray?: string[], orderBy?: string, facetKeys?: string[]) => void;
}

type BulkEditRow = { fieldConfigurationId: string; columnCode: string; value: string | string[] | null };

export const BulkEditModal: React.FC<BulkEditModalProps> = ({ isOpen, onClose, selectedItems, bulkUpdateReports, updateWorkOrdersOptimistically, setSelectedItems, t, fields, language, assignedUsers, refreshData }) => {
  const [rows, setRows] = useState<BulkEditRow[]>([
    { fieldConfigurationId: '', columnCode: '', value: '' }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [multiSelectIdx, setMultiSelectIdx] = useState<number | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { trackWorkflowEvent } = useWorkflowTracking();
  const { configuration, currentStage, stageFilters, globalFilters } = useWorkflow();

  const handleAddRow = () => {
    setRows([...rows, { fieldConfigurationId: '', columnCode: '', value: '' }]);
  };

  // Helper to reset rows to initial state
  const resetRows = () => setRows([{ fieldConfigurationId: '', columnCode: '', value: '' }]);

  const handleRemoveRow = (idx: number) => {
    if (rows.length === 1) {
      // If only one row, clear it instead of removing
      setRows([{ fieldConfigurationId: '', columnCode: '', value: '' }]);
    } else {
      setRows(rows.filter((_, i) => i !== idx));
    }
  };

  const handleFieldChange = (idx: number, fieldId: string) => {
    const field = fields.find(f => f.fieldConfigurationId === fieldId);
    setRows(rows.map((row, i) =>
      i === idx
        ? { ...row, fieldConfigurationId: fieldId, columnCode: field ? field.columnCode : '', value: '' }
        : row
    ));
  };

  const handleValueChange = (idx: number, value: string | string[] | null) => {
    setRows(rows.map((row, i) => (i === idx ? { ...row, value } : row)));
  };

  // Format date for saving - convert local midnight to UTC
  const formatDateForSave = (dateStr: string | null) => {
    if (!dateStr) return null
    // Parse as local date at midnight
    const [year, month, day] = dateStr.split('-').map(Number)
    const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
    // Convert to ISO string (which is in UTC)
    return localDate.toISOString()
  };

  // Helper to get dropdown options for a field
  function getDropdownOptions(field: FieldConfiguration) {
    if (field.columnCode === 'AssignedToUser' || field.columnCode === 'AssignedToUser/Email') {
      return assignedUsers
        .sort((a, b) => (a.fullName || '').toLowerCase().localeCompare((b.fullName || '').toLowerCase()))
        .map((user, idx) => ({ value: user.email, label: user.fullName, key: `${user.email}-${idx}` }));
    }
    if (Array.isArray(field.customValues) && field.customValues.length > 0) {
      return field.customValues.map((opt, idx) => ({
        value: opt.code,
        label: language === 'fr' ? (opt.titleFrench || opt.code) : (opt.titleEnglish || opt.code),
        color: opt.color,
        key: `${opt.code}-${idx}`
      }));
    }
    return [];
  }

  // Helper to get multi-select options for textList
  function getTextListOptions(field: FieldConfiguration) {
    return (field.customValues || []).map(opt => ({
      value: opt.code,
      label: language === 'fr' ? (opt.titleFrench || opt.code) : (opt.titleEnglish || opt.code),
      color: opt.color,
    }));
  }
  
  // Handle save from MultiSelectCell
  const handleMultiSelectSave = (selected: string[]) => {
    if (multiSelectIdx !== null) {
      setRows(rows.map((row, i) => i === multiSelectIdx ? { ...row, value: selected } : row));
      setMultiSelectIdx(null);
    }
  };
  const handleMultiSelectCancel = () => setMultiSelectIdx(null);

  const handleSubmit = () => {
    // Validate: at least one field selected (value can be empty/null to clear)
    const hasField = rows.some(row => row.fieldConfigurationId);
    if (!hasField) {
      toast({
        title: t('noFieldsSelected') || 'No fields selected',
        description: t('pleaseSelectAtLeastOneField') || 'Please select at least one field.',
        variant: 'destructive',
      });
      return;
    }
    
    // Show confirmation dialog (it will check for empty values and warn)
    setShowConfirmDialog(true);
  };

  const performBulkUpdate = async () => {
    // Build updates array
    const updates = selectedItems.map(id => {
      const updateObj: Record<string, any> = {};
      rows.filter(r => r.fieldConfigurationId).forEach(r => {
        const field = fields.find(f => f.fieldConfigurationId === r.fieldConfigurationId);
        if (!field) return;
        
        const fieldName = field.columnCode.charAt(0).toLowerCase() + field.columnCode.slice(1);
        
        if (field.columnCode === 'AssignedToUser' || field.columnCode === 'AssignedToUser/Email') {
          // Find user object by email
          const user = assignedUsers.find(u => u.email === r.value || u.email === (Array.isArray(r.value) ? r.value[0] : undefined));
          if (user) {
            updateObj[fieldName] = { email: user.email, fullName: user.fullName };
          } else if (r.value === null || r.value === '') {
            updateObj[fieldName] = null;
          }
        } else if (isTextListField(field)) {
          // For TextList: empty array if null/empty, otherwise the array
          if (r.value === null || r.value === '' || (Array.isArray(r.value) && r.value.length === 0)) {
            updateObj[fieldName] = [];
          } else {
            updateObj[fieldName] = Array.isArray(r.value) ? r.value : [r.value];
          }
        } else if (isDateField(field)) {
          if (r.value === null || r.value === '') {
            updateObj[fieldName] = null;
          } else {
            updateObj[fieldName] = formatDateForSave(typeof r.value === 'string' ? r.value : null);
          }
        } else if (isNumberField(field)) {
          let val = Array.isArray(r.value) ? r.value[0] : r.value;
          if (val === null || val === '' || val === undefined) {
            updateObj[fieldName] = null;
          } else if (typeof val === 'string' && val.trim() !== '') {
            let parsed: number;
            if (field.columnTypeCode?.toLowerCase() === 'number') {
              parsed = parseInt(val, 10);
            } else {
              parsed = parseFloat(val);
            }
            if (!isNaN(parsed)) {
              updateObj[fieldName] = parsed;
            }
          }
        } else {
          // For all other fields (including boolean): null if empty, otherwise the value
          if (r.value === null || r.value === '' || r.value === undefined) {
            updateObj[fieldName] = null;
          } else {
            updateObj[fieldName] = r.value;
          }
        }
      });
      return { id, updates: updateObj };
    }).filter(u => Object.keys(u.updates).length > 0);
    
    if (updates.length === 0) {
      toast({
        title: t('noFieldsSelected') || 'No fields selected',
        description: t('pleaseSelectAtLeastOneField') || 'Please select at least one field and value.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsSubmitting(true);
    // Don't close confirmation dialog until we know the result
    
    try {
      // Call the API first (don't update UI until we know it succeeded)
      const result = await bulkUpdateReports(updates);
      
      // Handle the new response structure
      const { successUpdateCount, failedUpdateCount, messages } = result;
      
      // Show detailed success/failure message
      if (failedUpdateCount === 0) {
        // All successful
        toast({
          variant: "success",
          title: t('success') || 'Success',
          description: t('bulkUpdateSuccess')?.replace('{count}', String(successUpdateCount)) || 
                      `Successfully updated ${successUpdateCount} record${successUpdateCount !== 1 ? 's' : ''}.`,
        });
      } else if (successUpdateCount === 0) {
        // All failed
        const errorDetails = messages && messages.length > 0 
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}` 
          : '';
        toast({
          variant: "destructive",
          title: t('error') || 'Error',
          description: t('bulkUpdateFailed')?.replace('{count}', String(failedUpdateCount)).replace('{details}', errorDetails) || 
                      `Failed to update ${failedUpdateCount} record${failedUpdateCount !== 1 ? 's' : ''}${errorDetails}`,
        });
      } else {
        // Mixed results
        const errorDetails = messages && messages.length > 0 
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}` 
          : '';
        toast({
          variant: "default",
          title: t('partialSuccess') || 'Partial Success',
          description: t('bulkUpdatePartial')?.replace('{success}', String(successUpdateCount)).replace('{failed}', String(failedUpdateCount)).replace('{details}', errorDetails) || 
                      `Successfully updated ${successUpdateCount} record${successUpdateCount !== 1 ? 's' : ''}. ${failedUpdateCount} record${failedUpdateCount !== 1 ? 's' : ''} failed${errorDetails}`,
        });
      }
      
      // Track the bulk edit action
      trackWorkflowEvent('Click Tracking', {
        fieldsUpdated: rows.filter(r => r.fieldConfigurationId && r.value !== undefined).map(r => {
          const field = fields.find(f => f.fieldConfigurationId === r.fieldConfigurationId);
          return field ? field.columnCode : 'unknown';
        }),
        action: 'bulk_edit',
        successCount: successUpdateCount,
        failedCount: failedUpdateCount
      });

      // Update UI optimistically for immediate feedback if there were successful updates
      if (successUpdateCount > 0) {
        updateWorkOrdersOptimistically(updates);
      }
      
      // Only close modal and refresh data if there were any successful updates
      if (successUpdateCount > 0) {
        setShowConfirmDialog(false);
        setRows([{ fieldConfigurationId: '', columnCode: '', value: '' }]);
        setSelectedItems([]);
        onClose();

        // Refresh data to show updated records - with a small delay to ensure server has processed
        const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
        const currentStageFilters = stageFilters.filter(f => f.stageId === stageConfig?.stageId);
        const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
        const baseFilter = `Stage eq '${currentStage}'`;
        const filterArray = allFilters.length > 0 
          ? [`${baseFilter} and ${allFilters.join(' and ')}`]
          : [baseFilter];
        
        const facetKeys = getFacetKeysFromConfiguration(configuration);
        
        // First refresh immediately
        refreshData(filterArray, undefined, facetKeys);
        
        // Second refresh after a short delay to ensure server data is fully updated
        setTimeout(() => {
          refreshData(filterArray, undefined, facetKeys);
        }, 500);
      } else {
        // If all failed, close confirmation dialog but keep main modal open so user can retry
        setShowConfirmDialog(false);
        
        // Refresh data even on failures to show correct state
        const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
        const currentStageFilters = stageFilters.filter(f => f.stageId === stageConfig?.stageId);
        const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
        const baseFilter = `Stage eq '${currentStage}'`;
        const filterArray = allFilters.length > 0 
          ? [`${baseFilter} and ${allFilters.join(' and ')}`]
          : [baseFilter];
        
        const facetKeys = getFacetKeysFromConfiguration(configuration);
        
        refreshData(filterArray, undefined, facetKeys);
      }
    } catch (error: any) {
      setShowConfirmDialog(false);
      toast({
        title: t('bulkUpdateError') || 'Bulk update failed',
        description: error?.message || t('bulkUpdateErrorDesc') || 'An error occurred during bulk update.',
        variant: 'destructive',
      });
      
      // On error, refresh data to restore the correct state
      const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
      const currentStageFilters = stageFilters.filter(f => f.stageId === stageConfig?.stageId);
      const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
      const baseFilter = `Stage eq '${currentStage}'`;
      const filterArray = allFilters.length > 0 
        ? [`${baseFilter} and ${allFilters.join(' and ')}`]
        : [baseFilter];
      
      const facetKeys = getFacetKeysFromConfiguration(configuration);
      
      refreshData(filterArray, undefined, facetKeys);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:w-[90vw] max-w-2xl min-w-[300px] sm:min-w-[600px]" ref={modalContentRef}>
        <DialogHeader>
          <DialogTitle>{t('editWorkItems') || 'Edit work items'}</DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-700 text-2xl"
              aria-label={t('close') || 'Close'}
              onClick={onClose}
            >
            </button>
          </DialogClose>
        </DialogHeader>
        <div className="flex flex-col gap-4 mt-4">
          {/* Header row */}
          <div className="flex items-end gap-3 w-full text-sm font-semibold pb-1">
            <div className="w-8" />
            <div className="flex-1 text-left">{t('field') || 'Field'} <span className="text-red-500">*</span></div>
            <div className="w-8" />
            <div className="flex-1 text-left">{t('value') || 'Value'}</div>
          </div>
          {/* Separator */}
          <div className="w-full border-b border-gray-700 mb-2" />
          {/* Field rows */}
          {rows.map((row, idx) => {
            const field = fields.find(f => f.fieldConfigurationId === row.fieldConfigurationId);
            return (
              <div className="flex items-center gap-3 w-full" key={idx}>
                <button type="button" className="text-green-500 text-xl px-1" title="Add field" onClick={handleAddRow}>
                  +
                </button>
                <button type="button" className="text-red-500 text-xl px-1" title="Remove field" onClick={() => handleRemoveRow(idx)}>
                  ×
                </button>
                <div className="flex flex-col flex-1">
                  <select
                    id={`bulk-field-select-${idx}`}
                    className="border rounded px-2 py-1 w-full bg-transparent"
                    value={row.fieldConfigurationId}
                    onChange={e => handleFieldChange(idx, e.target.value)}
                  >
                    <option value="" disabled>{t('selectOption') || 'Select option'}</option>
                    {fields
                      .filter(field =>
                        // Only show fields not already selected in other rows
                        !rows.some((r, i) => i !== idx && r.fieldConfigurationId === field.fieldConfigurationId)
                      )
                      .map(field => (
                        <option key={field.fieldConfigurationId} value={field.fieldConfigurationId}>
                          {language === 'fr' ? field.columnNameFrench : field.columnNameEnglish}
                        </option>
                      ))}
                  </select>
                </div>
                <span className="mx-2 text-lg">=</span>
                <div className="flex flex-col flex-1">
                  {/* Value field logic */}
                  {field ? (
                    isTextListField(field) ? (
                      <>
                        <button
                          type="button"
                          className="border rounded px-2 py-1 w-full bg-transparent text-left"
                          onClick={() => setMultiSelectIdx(idx)}
                        >
                          {Array.isArray(row.value) && row.value.length > 0
                            ? row.value.map((v: string) => {
                                const opt = getTextListOptions(field).find(o => o.value === v);
                                return opt ? opt.label : v;
                              }).join(', ')
                            : t('selectOptions') || 'Select options'}
                        </button>
                        {multiSelectIdx === idx && (
                          <MultiSelectCell
                            value={Array.isArray(row.value) ? row.value : []}
                            options={getTextListOptions(field)}
                            column={field}
                            rowId={'' + idx}
                            onSave={handleMultiSelectSave}
                            onCancel={handleMultiSelectCancel}
                          />
                        )}
                      </>
                    ) : isDateField(field) ? (
                      <ModernDatePicker
                        value={typeof row.value === 'string' ? row.value : (row.value === null ? '' : '')}
                        onChange={val => handleValueChange(idx, val || null)}
                        placeholder={t('selectDate') || 'Select date'}
                        portalContainer={modalContentRef.current}
                      />
                    ) : isNumberField(field) ? (
                      <input
                        id={`bulk-value-input-${idx}`}
                        className="border rounded px-2 py-1 w-full bg-transparent"
                        type="number"
                        value={Array.isArray(row.value) ? (row.value[0] ?? '') : (row.value ?? '')}
                        onChange={e => handleValueChange(idx, e.target.value)}
                        placeholder={t('enterValue') || 'Enter value'}
                        step={field.columnTypeCode?.toLowerCase() === 'number' ? '1' : 'any'}
                      />
                    ) : isDropdownField(field) ? (
                      <CustomDropdownField
                        options={getDropdownOptions(field)}
                        selectedValues={Array.isArray(row.value) ? row.value : row.value ? [row.value] : []}
                        onChange={vals => handleValueChange(idx, vals.length > 0 ? vals[0] : null)}
                        placeholder={t('selectOption') || 'Select option'}
                        useColorCode={!!(field.customValues && field.customValues.length > 0)}
                        multiSelect={false}
                        portalContainer={modalContentRef.current}
                        allowClear={!!(field.customValues && field.customValues.length > 0)}
                      />
                    ) : isBooleanField(field) ? (
                      <CustomDropdownField
                        options={defaultBooleanOptions.map((opt, idx) => ({
                          value: opt.code,
                          label: language === 'fr' ? opt.titleFrench : opt.titleEnglish,
                          key: `${opt.code}-${idx}`
                        }))}
                        selectedValues={row.value == null ? [] : Array.isArray(row.value) ? row.value : [row.value]}
                        onChange={vals => handleValueChange(idx, vals.length > 0 ? vals[0] : null)}
                        placeholder={t('selectOption') || 'Select option'}
                        useColorCode={false}
                        multiSelect={false}
                        portalContainer={modalContentRef.current}
                        allowClear={true}
                      />
                    ) : (
                      <input
                        id={`bulk-value-input-${idx}`}
                        className="border rounded px-2 py-1 w-full bg-transparent"
                        value={Array.isArray(row.value) ? row.value.join(', ') : (row.value ?? '')}
                        onChange={e => handleValueChange(idx, e.target.value)}
                        placeholder={t('enterValue') || 'Enter value'}
                      />
                    )
                  ) : (
                    <input
                      id={`bulk-value-input-${idx}`}
                      className="border rounded px-2 py-1 w-full bg-transparent"
                      value={Array.isArray(row.value) ? row.value.join(', ') : (row.value ?? '')}
                      onChange={e => handleValueChange(idx, e.target.value)}
                      placeholder={t('enterValue') || 'Enter value'}
                      disabled
            />
          )}
        </div>
              </div>
            );
          })}
          <Button variant="outline" type="button" className="w-fit" onClick={handleAddRow}>
            + {t('addNewField') || 'Add new field'}
          </Button>
        </div>
        <DialogFooter className="mt-6 flex justify-end gap-2">
          <Button variant="elliptical-outline" type="button" onClick={() => { resetRows(); onClose(); }} disabled={isSubmitting}>{t('cancel') || 'Cancel'}</Button>
          <Button variant="elliptical" type="button" onClick={handleSubmit} disabled={isSubmitting}>{t('ok') || 'OK'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Confirmation Dialog */}
    <Dialog open={showConfirmDialog} onOpenChange={() => setShowConfirmDialog(false)}>
      <DialogContent className="w-[95vw] sm:w-[90vw] max-w-md min-w-[280px] sm:min-w-[350px]">
        <DialogHeader>
          <DialogTitle>{t('confirmUpdate') || 'Confirm Update'}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-gray-600">
            {t('confirmUpdateMessage')?.replace('{count}', String(selectedItems.length)) || `You are about to update ${selectedItems.length} items. This action cannot be undone.`}
          </p>
          {rows.some(row => row.fieldConfigurationId && (row.value === null || row.value === '' || (Array.isArray(row.value) && row.value.length === 0))) && (
            <p className="text-amber-600 mt-3 text-sm">
              {t('someValuesEmpty') || 'Some values are empty and will clear the existing data.'}
            </p>
          )}
        </div>
        <DialogFooter className="flex justify-end gap-2">
          <Button 
            variant="elliptical-outline" 
            onClick={() => setShowConfirmDialog(false)}
            disabled={isSubmitting}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button 
            variant="elliptical"
            onClick={performBulkUpdate}
            disabled={isSubmitting}
          >
            {t('confirm') || 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}; 